import { useState, useMemo } from 'react';
import useDriverAssignments from '../../hooks/useDriverAssignments';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from 'react-router-dom';

export default function DriverAssignmentView() {

    const [orderId, setOrderId] = useState('');
    const [status, setStatus] = useState('');
    const [driverName, setDriverName] = useState('');
    const [page, setPage] = useState(1);
    const perPage = 20;

    const { assignments = [], pagination, isLoading, error } =
        useDriverAssignments({
            query: {
                ...(orderId && { order_id: orderId }),
                ...(status && { status }),
            },
            page,
            perPage
        });

    // 🔥 FRONTEND DRIVER NAME FILTER
    const filteredAssignments = useMemo(() => {
        if (!driverName) return assignments;

        return assignments.filter(a =>
            a.driver_name
                ?.toLowerCase()
                .includes(driverName.toLowerCase())
        );
    }, [assignments, driverName]);

    const totalPages =
        pagination?.last_page ||
        pagination?.total_pages ||
        pagination?.total ||
        1;

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const displayValue = (value) =>
        value === null || value === undefined || value === ''
            ? 'N/A'
            : value;

    const getStatusBadgeStyle = (status) => {
        const styles = {
            ongoing: {
                backgroundColor: 'rgb(13 110 253 / 20%)',
                color: '#0d6efd',
            },
            completed: {
                backgroundColor: 'rgb(25 135 84 / 20%)',
                color: '#198754',
            }
        };

        return {
            display: 'inline-block',
            padding: '5px 8px',
            borderRadius: '5px',
            fontSize: '12px',
            fontWeight: '700',
            textTransform: 'capitalize',
            ...(styles[status] || {})
        };
    };

    if (error) return <div>Error loading driver assignments</div>;

    return (
        <div className="container-fluid px-0">
            <div className="row g-0">
                <div className="col-12">

                    {/* Header */}
                    <div className="title-header option-title d-flex justify-content-between align-items-center p-3">
                        <h5 className="mb-0">Driver Assignment View</h5>

                        <div className="d-flex align-items-center" style={{ gap: 16 }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by Order ID"
                                value={orderId}
                                onChange={e => setOrderId(e.target.value)}
                                style={{ maxWidth: 160 }}
                            />

                            {/* 🔥 DRIVER NAME FILTER */}
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by Driver Name"
                                value={driverName}
                                onChange={e => setDriverName(e.target.value)}
                                style={{ maxWidth: 180 }}
                            />

                            <select
                                className="form-select"
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                style={{ maxWidth: 140 }}
                            >
                                <option value="">All Statuses</option>
                                <option value="ongoing">Ongoing</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                        <table className="table all-package order-table theme-table mb-0">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Customer Name</th>
                                    <th>Driver Name</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-end">Amount</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4">
                                            <div className="spinner-border" />
                                        </td>
                                    </tr>
                                ) : filteredAssignments.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                            No driver assignments found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAssignments.map((assignment) => (
                                        <tr key={assignment.assignment_id || assignment.order_id}>
                                            <td>{displayValue(assignment.order_id)}</td>
                                            <td>{displayValue(assignment.customer_name)}</td>
                                            <td>{displayValue(assignment.driver_name)}</td>

                                            <td className="text-center">
                                                {assignment.status ? (
                                                    <span style={getStatusBadgeStyle(assignment.status)}>
                                                        {assignment.status}
                                                    </span>
                                                ) : 'N/A'}
                                            </td>

                                            <td className="text-end">
                                                <strong>
                                                    {displayValue(assignment.currency_symbol)}{' '}
                                                    {displayValue(assignment.total_amount)}
                                                </strong>
                                            </td>

                                            <td className="text-center">
                                                <Link
                                                    className="btn btn-sm btn-outline-primary"
                                                    to={`/orderdeatils/${assignment.order_id}`}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <nav className="d-flex justify-content-center mt-4">
                        <ul className="pagination">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page - 1)}
                                >
                                    « Prev
                                </button>
                            </li>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                                <li
                                    key={pg}
                                    className={`page-item ${pg === page ? 'active' : ''}`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pg)}
                                    >
                                        {pg}
                                    </button>
                                </li>
                            ))}

                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page + 1)}
                                >
                                    Next »
                                </button>
                            </li>
                        </ul>
                    </nav>

                </div>
            </div>
        </div>
    );
}
